import { Play } from "lucide-react";
import { useEffect, useState } from "react";
import type { GameCategory } from "../../data/games";
import styles from "./GameArtwork.module.css";

interface GameArtworkProps {
  imageUrl?: string;
  title: string;
  fallbackLabel: string;
  categoryId: GameCategory;
  priority?: boolean;
}

type ImageStatus = "loading" | "loaded" | "error";

export function GameArtwork({
  imageUrl,
  title,
  fallbackLabel,
  categoryId,
  priority = false,
}: GameArtworkProps) {
  const [imageStatus, setImageStatus] = useState<ImageStatus>(
    imageUrl ? "loading" : "error",
  );

  useEffect(() => {
    setImageStatus(imageUrl ? "loading" : "error");
  }, [imageUrl]);

  const handleLoad = () => setImageStatus("loaded");
  const handleError = () => setImageStatus("error");

  const isFallbackOnly = imageStatus === "error";

  return (
    <div
      className={styles.artwork}
      data-category={categoryId}
      role={isFallbackOnly ? "img" : undefined}
      aria-label={isFallbackOnly ? `${title} – illustrerat spelomslag` : undefined}
    >
      <div className={styles.fallback} aria-hidden="true">
        <span className={styles.orbit} />
        <span className={styles.playMark}>
          <Play aria-hidden="true" fill="currentColor" />
        </span>
        <span className={styles.fallbackLabel}>{fallbackLabel}</span>
        <span className={styles.fallbackBrand}>PARTYCAST</span>
      </div>

      {imageUrl && imageStatus !== "error" ? (
        <img
          className={`${styles.image} ${
            imageStatus === "loaded" ? styles.imageLoaded : ""
          }`}
          src={imageUrl}
          alt={`${title} – spelomslag`}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={priority ? "high" : "auto"}
          onLoad={handleLoad}
          onError={handleError}
        />
      ) : null}
    </div>
  );
}
